import CountryStateSection from "@components/CountryStateSection/CountryStateSection";
import FormField from "@components/Form/FormFields/FormFields";
import FormErrorMessage from "@components/FormErrorMessage/FormErrorMessage";
import FormPrivacySection from "@components/FormPrivacySection/FormPrivacySection";
import Grid from "@components/Grid/Grid";
import Loader from "@components/Loader/Loader";
import SubmitButton from "@components/SubmitButton/SubmitButton";
import { useSettings } from "@utilities/context/settings";
import { getBusinessUnit } from "@utilities/getBusinessUnit";
import { handleSubmitFormErrors } from "@utilities/handleSubmitFormErrors";
import {
  findMatchingSelectId,
  findMatchingSelectValue,
} from "@utilities/mapSelectValue";
import setGADataLayer from "@utilities/setGADataLayer";
import clsx from "clsx";
import { useRouter } from "next/router";
import { Fragment, useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { LOGIN_URL } from "../../constants";
import { UserContext } from "../../context/user";
import {
  getBroadIndustries,
  getPersonalDetails,
  getPreferredDates,
  getReasonForReservation,
  getTitles,
  setReserveAPlaceLoggedIn,
  setReserveAPlaceNotLoggedIn,
} from "../../services/forms";
import styles from "./C74_ReserveAPlace.module.scss";
import { getFormInitialValues, permanentFields } from "./InitialData";
import { ProfileDetailsProps, SelectItem } from "./types";

import { getRedirectUrl } from "../../utils/redirects/sitecoreRedirects";

let firstInteract = 0;

export const C74_ReserveAPlace = (props: { content: ProfileDetailsProps }) => {
  const { content } = props;
  const {
    registerFormTitle,
    failedMessage,
    registerFormLoggedInTitleText,
    welcomeBackMessageForLoggedInUser,
    introText,
    introLoggedInText,
  } = content;
  const router = useRouter();

  const isWindow = typeof window === "object";
  const urlParams = isWindow
    ? new URLSearchParams(window.location.search)
    : null;

  const pathname = isWindow ? window.location.pathname : null;

  const itemId = urlParams?.get("itemId");

  if (itemId !== null) {
    const sitecoreRedirectUrl = getRedirectUrl(pathname, urlParams);

    if (sitecoreRedirectUrl !== null) {
      router.push(sitecoreRedirectUrl);
    }
  }

  const [submittedUserEmail, setSubmittedUserEmail] = useState<string>("");
  const [showNationalCountriesList, setShowNationalCountriesList] =
    useState<boolean>(false);
  const [countryInputValue, setCountryInputValue] = useState("");
  const [countriesList, setCountriesList] = useState<string[]>(null);
  const [userPersonalDetails, setUserPersonalDetails] =
    useState<Record<string, unknown>>(null);
  const [preferredDates, setPreferredDates] = useState<SelectItem[]>([]);
  const [reasons, setReasons] = useState<SelectItem[]>(null);
  const [titles, setTitles] = useState<SelectItem[]>(null);
  const [industries, setIndustries] = useState<SelectItem[]>(null);
  const [error, setError] = useState<boolean>(false);
  const [handledSubmitFormErrors, setHandledSubmitFormErrors] =
    useState<Array<string> | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const { user } = useContext(UserContext);
  const [programmeTypeCode, setProgrammeTypeCode] = useState<string>("");

  const [loggedInUser, setLoggedInUser] = useState<boolean | null>(null);
  const [canContactByEmail, setCanContactByEmail] = useState<boolean>(false);
  const { apiErrorMessages } = useSettings();

  useEffect(() => {
    setProgrammeTypeCode(router?.query?.programmeTypeCode as string);
  }, [router?.query?.programmeTypeCode]);

  useEffect(() => {
    if (user) {
      setLoggedInUser(true);
    } else {
      setLoggedInUser(null);
    }
  }, [user]);

  const { register, handleSubmit, formState, watch, setValue, control } =
    useForm({ mode: "onBlur" });

  const setLoggedInUserInitialData = async (industries: SelectItem[]) => {
    const personalDetails = await getPersonalDetails();
    if (personalDetails?.result) {
      const {
        emailAddress,
        firstName,
        lastName,
        employment,
        address,
        broadIndustry,
        telephoneNumber,
        yearsOfManagerialExperience,
      } = personalDetails.result;

      setValue("emailAddress", emailAddress);
      setValue("firstName", firstName);
      setValue("lastName", lastName);
      setValue("emailAddress", emailAddress);
      setValue("companyName", employment?.company);
      setValue("homeCountry", address?.homeCountry);
      setValue("industry", findMatchingSelectValue(industries, broadIndustry));
      setValue("telephoneNumber", telephoneNumber);
      setValue("yearsManagerialExperience", yearsOfManagerialExperience);
      setUserPersonalDetails(personalDetails);
      setCanContactByEmail(
        loggedInUser && personalDetails?.result?.isStayInformed
      );
    }
  };

  const prepareFormData = async (loggedInUser: boolean) => {
    try {
      const preferredDates: SelectItem[] =
        await getPreferredDates(programmeTypeCode);
      const reasons: SelectItem[] = await getReasonForReservation();
      const industries: SelectItem[] = await getBroadIndustries();
      const titles: SelectItem[] = await getTitles();
      setIndustries(industries);
      setTitles(titles);
      if (preferredDates && preferredDates.length) {
        setPreferredDates(preferredDates);
        setValue("preferredDates", preferredDates[0].value);
      }
      if (reasons && reasons.length) {
        setReasons(reasons);
        setValue("reasons", reasons[0].value);
      }

      if (loggedInUser) {
        setLoggedInUserInitialData(industries);
      }
    } catch (_e) {
      setError(true);
    }
  };

  useEffect(() => {
    if (programmeTypeCode) {
      prepareFormData(loggedInUser);
    }
  }, [programmeTypeCode, loggedInUser]);

  const FormFields = getFormInitialValues(
    content,
    userPersonalDetails,
    loggedInUser,
    preferredDates,
    titles,
    industries,
    reasons,
    watch
  );

  const redirectToSuccessPage = () => {
    setGADataLayer({
      event: "formComplete",
      formName: "reserve a place",
      programmeCode: `${router.query.programme || ""}`,
      businessUnit: getBusinessUnit(),
      eventType: "",
    });

    router.push(
      `/reserve-a-place-success?returnToPreviousPageUrl=${router?.query?.referPage}&referPage=${router?.query?.referPage}`
    );
  };

  useEffect(() => {
    const subscription = watch((_e, a) => {
      if (a.type === "change" && firstInteract < 1) {
        setGADataLayer({
          event: "formStart",
          formName: "reserve a place",
        });
        firstInteract++;
      }
      return () => subscription.unsubscribe();
    });

    return () => subscription.unsubscribe();
  }, [watch]);

  const onSubmit = async (data) => {
    setLoading(true);
    setHandledSubmitFormErrors(null);
    const submittedData = {
      profile: {
        isStayInformed: canContactByEmail || data?.isStayInformed === "true",
        emailAddress: data.emailAddress,
        title: findMatchingSelectId(titles, data.title),
        firstName: data.firstName,
        lastName: data.lastName,
        password: data.password,
        industry: findMatchingSelectId(industries, data.industry),
        yearsOfManagerialExperience: data.yearsManagerialExperience,
      },
      programmeType: {
        isProgrammeTypeRequired: true,
        programmeTypeCode: programmeTypeCode,
        programmeTypeGroup: router?.query?.programmeTypeGroup,
      },
      preferredDateId: findMatchingSelectId(
        preferredDates,
        data.preferredDates
      ),
      company: data.companyName,
      yearsOfManagerialExperience: data.yearsManagerialExperience,
      industry: findMatchingSelectId(industries, data.industry),
      telephoneNumber: data.telephoneNumber ? data.telephoneNumber : null,
      homeCountry: data.homeCountry,
      objectives: data.objectivesTextLabelText,
      whatLedYouToReserveAplace: findMatchingSelectId(reasons, data.reasons),
    };
    try {
      setSubmittedUserEmail(data.emailAddress);
      const setReserveAPlace = loggedInUser
        ? setReserveAPlaceLoggedIn
        : setReserveAPlaceNotLoggedIn;
      const response = await setReserveAPlace(
        submittedData,
        sessionStorage.getItem("crm_campaign")
      );

      if (response.status === 201) {
        redirectToSuccessPage();
      } else {
        const handledSubmitFormErrors = handleSubmitFormErrors(
          response,
          apiErrorMessages
        );
        window.scrollTo(0, 0);
        setHandledSubmitFormErrors(handledSubmitFormErrors);
      }
      setLoading(false);
    } catch (_e) {
      const handledSubmitFormErrors = handleSubmitFormErrors(
        null,
        apiErrorMessages
      );
      setHandledSubmitFormErrors(handledSubmitFormErrors);
      setLoading(false);
    }
  };

  const FieldJSX = ({ labelText = "", dataText }) => (
    <div className={styles["field-row"]}>
      <label>{labelText}:</label>
      {dataText ? <p>{dataText}</p> : ""}
    </div>
  );

  const renderFormSection = () => {
    if (!programmeTypeCode || (loggedInUser && !userPersonalDetails))
      return (
        <div className={styles.spinner}>
          <Loader
            color="#001e62"
            size={70}
            aria-label="Loading Spinner"
            data-testid="loader"
          />
        </div>
      );

    return (
      <section className={styles["form-group-wrapper"]}>
        <form method="POST" className="form" onSubmit={handleSubmit(onSubmit)}>
          <div className={styles["form-row"]} />
          {FormFields.map((field: any, index) => {
            if (field?.isHidden) return null;

            if (field.propertyName === "homeCountry") {
              return (
                <CountryStateSection
                  key={`${field.labelText}${index}`}
                  isNationalityVariant={true}
                  register={register}
                  control={control}
                  setValue={setValue}
                  formState={formState}
                  countryInputValue={countryInputValue}
                  setCountryInputValue={setCountryInputValue}
                  countries={countriesList}
                  setCountries={setCountriesList}
                  selectedCountry={watch(field?.propertyName) as string}
                  setSelectedCountry={(countryVal) => {
                    setValue(field?.propertyName, countryVal);
                  }}
                  showCountriesList={showNationalCountriesList}
                  setShowCountriesList={setShowNationalCountriesList}
                  countryOfResidenceLabelText={field.labelText}
                  countryValidationMsg={field.validationMsg}
                />
              );
            }

            return (
              <Fragment key={`container-${field?.labelText}-${index}`}>
                {loggedInUser &&
                permanentFields.includes(field.propertyName) ? (
                  <FieldJSX
                    key={`${field?.labelText}-${index}`}
                    labelText={field?.labelText}
                    dataText={field?.dataText}
                  />
                ) : (
                  <FormField
                    formType={field?.formType}
                    watch={watch}
                    validation={field?.validation}
                    type={field?.type || "text"}
                    register={register}
                    property={field?.dataText}
                    key={`${field.labelText}${index}`}
                    hintText={field?.hintText}
                    placeholder={field.labelText}
                    options={field?.options}
                    optionalText={field?.optionalText}
                    name={field?.propertyName}
                    errors={formState.errors}
                    setSelectValue={setValue}
                    selectValue={watch(field?.propertyName) as string}
                    firstOptionTextForSelectField={
                      field?.firstOptionTextForSelectField
                    }
                    isEmptyFirstOption={field?.isEmptyFirstOption}
                  />
                )}
              </Fragment>
            );
          })}

          {!canContactByEmail && (
            <FormPrivacySection register={register} formState={formState} />
          )}

          <div className="btn-and-text-wrapper">
            <SubmitButton loading={loading} id="submitButton" text="Submit" />
          </div>
        </form>
      </section>
    );
  };

  return (
    <div className={clsx(styles["edit-details"], "wrapper component-wrapper")}>
      <Grid row>
        <Grid column sm={12} md={6}>
          <div className={clsx(styles.component, "component", styles.form)}>
            <h1 className="h2">
              {loggedInUser ? registerFormLoggedInTitleText : registerFormTitle}
            </h1>
            {loggedInUser && welcomeBackMessageForLoggedInUser && (
              <h2>{welcomeBackMessageForLoggedInUser}</h2>
            )}
            {error && (
              <FormErrorMessage
                failedRichText={failedMessage?.fields?.content}
                failedTinyMceRichText={failedMessage?.fields?.contentTinyMce}
              />
            )}
            {handledSubmitFormErrors &&
              handledSubmitFormErrors.map((error, index) => (
                <FormErrorMessage
                  key={index}
                  text={error}
                  userEmail={submittedUserEmail}
                />
              ))}
          </div>
          <section
            className={clsx(
              styles.component,
              styles["my-profile"],
              styles["form"],
              "form",
              styles.cf
            )}
          >
            <div className={styles["fields-floated"]}>
              <Grid row>
                <Grid column sm={12}>
                  <p>{loggedInUser ? introLoggedInText : introText}</p>
                  <br />
                  <br />
                  {!loggedInUser && (
                    <p>
                      Already registered?{" "}
                      <a className="underline" href={LOGIN_URL}>
                        Log in here.
                      </a>
                    </p>
                  )}
                </Grid>
              </Grid>
              {renderFormSection()}
            </div>
          </section>
        </Grid>
      </Grid>
    </div>
  );
};

export default C74_ReserveAPlace;
